import axios from 'axios';

// Base API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'http://docagent.vcurd.com/api';
console.log('API URL:', API_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false, // Set to true if your API requires cookies
});

// Function to check token validity
const checkTokenValidity = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    // Decode the JWT token
    const tokenData = JSON.parse(atob(token.split('.')[1]));
    
    // Check if token is expired
    const isExpired = tokenData.exp * 1000 <= Date.now();
    if (isExpired) {
      console.log('Token has expired');
      return false;
    }
    
    // Check if user_id exists in the token
    if (!tokenData.user_id && !tokenData.id) {
      console.log('User ID missing from token');
      return false;
    }
    
    return true;
  } catch (e) {
    console.error('Error validating token:', e);
    return false;
  }
};

// Add request interceptor to include auth token in requests
api.interceptors.request.use(
  (config) => {
    // Skip token validation for auth endpoints
    const isAuthEndpoint = config.url.includes('/auth/login.php') || 
                          config.url.includes('/auth/register.php') || 
                          config.url.includes('/auth/verify_email.php');
    
    if (!isAuthEndpoint) {
      // Validate token before making request
      if (!checkTokenValidity()) {
        console.log('Invalid or expired token detected in request interceptor');
        // Force logout by clearing storage and redirecting
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '#/login';
        
        // Reject the request with an error
        return Promise.reject(new Error('Invalid or expired token'));
      }
    }
  
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Clear local storage and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login
      window.location.href = '#/login';
    }
    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authAPI = {
  login: (credentials) => {
    console.log('Logging in with credentials');
    return api.post('/auth/login.php', credentials, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  register: (userData) => {
    console.log('Registering new user');
    return api.post('/auth/register.php', userData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  verifyEmail: (token, email) => {
    console.log('Verifying email with token and email');
    return api.get(`/auth/verify_email.php?token=${token}&email=${encodeURIComponent(email)}`);
  },
  
  resendVerification: (email) => {
    console.log('Resending verification email');
    return api.post('/auth/resend_verification.php', { email }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  forgotPassword: (email) => {
    console.log('Requesting password reset');
    return api.post('/auth/forgot_password.php', { email }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  resetPassword: (token, password) => {
    console.log('Resetting password with token');
    return api.post('/auth/reset_password.php', { token, password }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

// Documents API endpoints
export const documentsAPI = {
  getDocuments: (page = 1, limit = 10, search = '', projectId = null) => {
    console.log('Calling documents API', { page, limit, search, projectId });
    let url = `/documents/index.php?page=${page}&limit=${limit}`;
    
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (projectId) url += `&project_id=${projectId}`;
    
    return api.get(url);
  },
  
  getDocument: (id) => {
    console.log('Getting document with ID:', id);
    return api.get(`/documents/index.php?id=${id}`);
  },
  
  uploadDocument: (formData, config = {}) => {
    console.log('Uploading document');
    return api.post('/documents/upload.php', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config
    });
  },
  
  deleteDocument: (id) => {
    console.log('Deleting document with ID:', id);
    return api.delete(`/documents/delete.php?document_id=${id}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  downloadDocument: (id) => {
    console.log('Downloading document with ID:', id);
    return api.get(`/documents/download.php?id=${id}`, { responseType: 'blob' });
  },
  
  searchDocuments: (params) => {
    console.log('Searching documents with params:', params);
    return api.get('/documents/search.php', { params });
  },
  
  getDocumentTags: (documentId) => {
    console.log('Getting tags for document ID:', documentId);
    return api.get(`/documents/tags.php?document_id=${documentId}`);
  },
  
  updateDocumentTags: (documentId, tags) => {
    console.log('Updating tags for document ID:', documentId);
    return api.post('/documents/tags.php', { document_id: documentId, tags }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Update document tags and frameworks
  updateDocumentTagsAndFrameworks: (documentId, tags, frameworks) => {
    console.log('Updating tags and frameworks for document ID:', documentId);
    console.log('Tags being sent:', tags);
    console.log('Frameworks being sent:', frameworks);
    const payload = { document_id: documentId, tags, frameworks };
    console.log('Full payload:', payload);
    return api.post('/documents/tags.php', payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Get document analysis
  getDocumentAnalysis: (id) => {
    console.log('Getting document analysis for ID:', id);
    return api.get(`/documents/analysis.php?document_id=${id}`);
  },
  
  // Request document analysis
  requestAnalysis: (id) => {
    console.log('Requesting analysis for document ID:', id);
    return api.post('/documents/analyze.php', { document_id: id }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Query document with AI
  queryDocumentWithAI: (documentId, query) => {
    console.log('Querying document with AI, document ID:', documentId);
    return api.post('/documents/query.php', { document_id: documentId, query }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Chat with document
  chatWithDocument: (documentId, question) => {
    console.log('Chatting with document, document ID:', documentId);
    return api.post('/documents/chat.php', { document_id: documentId, question }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  // Get document chat history
  getChatHistory: (documentId) => {
    console.log('Getting chat history for document ID:', documentId);
    return api.post('/documents/get_chat_history.php', { document_id: documentId }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

// User API endpoints are defined below

// Projects API endpoints
export const projectsAPI = {
  getProjects: (page = 1, limit = 10) => {
    console.log('Calling projects API with URL:', `${API_URL}/api/projects?page=${page}&limit=${limit}`);
    return api.get(`/projects?page=${page}&limit=${limit}`);
  },
  
  getProject: (id) => {
    console.log('Getting project with ID:', id);
    return api.get(`/projects?id=${id}`);
  },
  
  createProject: (data) => {
    console.log('Creating project with data:', data);
    // Access the PHP file directly with POST method
    return api.post('/projects/create.php', data, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  updateProject: (id, data) => {
    console.log('Updating project with ID:', id);
    return api.put('/projects/update.php', { id, ...data }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  deleteProject: (id) => {
    console.log('Deleting project with ID:', id);
    return api.post('/projects/delete.php', { id }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  analyzeProject: (id) => {
    console.log('Analyzing project with ID:', id);
    return api.post('/projects/analyze.php', { project_id: id }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

// Users API endpoints
export const usersAPI = {
  getProfile: () => {
    console.log('Getting user profile');
    return api.get('/users/profile.php');
  },
  
  updateProfile: (userData) => {
    console.log('Updating user profile');
    return api.put('/users/update.php', userData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
  
  changePassword: (passwordData) => {
    console.log('Changing password');
    return api.post('/users/change_password.php', passwordData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },
};

// Dashboard API endpoints
export const dashboardAPI = {
  getDashboardData: () => {
    console.log('Calling dashboard API');
    return api.get('/dashboard/index.php');
  },
};

export default api;
